import { ChangeDetectionStrategy, Component, computed, input, InputSignal, model } from '@angular/core';

import { AbstractSelectComponent } from '@connect-services/src/lib/components/abstract-select.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export const NO_GROUP = 'no_group';
export type GroupBucket<T> = Record<string, SelectOption<T>[]>;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CheckboxComponent, RessourcesModule],
    selector: 'app-select-by-group',
    styleUrl: '../select/select.component.scss',
    templateUrl: './select-by-group.component.html',
})
export class SelectByGroupComponent<T> extends AbstractSelectComponent<T> {
    public readonly filteredOptionsByGroup = computed<GroupBucket<T>>(() => {
        if (!this.optionsByGroup()) {
            return {};
        }

        return this.filterOptionsByGroup();
    });
    public readonly labelNoGroup: InputSignal<string> = input<string>('other');
    public readonly optionsByGroup = model.required<GroupBucket<T> | null>();
    public readonly optionsOnly = computed(() => {
        if (!this.optionsByGroup()) {
            return [];
        }

        return Object.values(this.optionsByGroup()!).flat();
    });

    protected readonly NO_GROUP = NO_GROUP;
    protected readonly Object = Object;

    private filterOptionsByGroup(): GroupBucket<T> {
        const result: GroupBucket<T> = {};
        const sortedGroups = this.getSortedGroups();

        for (const groupName of sortedGroups) {
            const filteredItems = this.getFilteredItems(groupName);

            if (filteredItems.length) {
                result[groupName] = filteredItems;
            }
        }

        if (!Object.keys(result).length) {
            return {};
        }

        return result;
    }

    private getCheckboxState<T>(option: SelectOption<T>): CheckboxState {
        const selectedKeySet = new Set(this.selectedOptions().map((selectedOption) => selectedOption.label));

        return selectedKeySet.has(option.label) ? CheckboxState.CHECKED : CheckboxState.UNCHECKED;
    }

    private getFilteredItems(groupName: string): SelectOption<T>[] {
        const currentSearch = this.searchValue().trim().toLowerCase();

        return this.optionsByGroup()!
            [groupName].filter(
                (option) =>
                    option.label.toLowerCase().includes(currentSearch) ||
                    groupName.toLowerCase().includes(currentSearch),
            )
            .map((option: SelectOption<T>) => {
                return {
                    ...option,
                    state: this.getCheckboxState(option),
                };
            })
            .sort((a: SelectOption<T>, b: SelectOption<T>): number =>
                this.collator.compare(a.label.trim(), b.label.trim()),
            );
    }

    private getSortedGroups(): string[] {
        return Object.keys(this.optionsByGroup()!).sort((a: string, b: string) => {
            const aTrim = a.trim();
            const bTrim = b.trim();

            if (aTrim === NO_GROUP && bTrim !== NO_GROUP) {
                return 1;
            }
            if (bTrim === NO_GROUP && aTrim !== NO_GROUP) {
                return -1;
            }

            return this.collator.compare(aTrim, bTrim);
        });
    }
}
